/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import React from 'react';
import { getRenderingHelper } from '../utils/helpers';

export type IAddModuleProps = {
    moduleId: string;
    slotId: string;
    enabled: boolean;
    mode: AddControlMode;
    index?: number;
    slotName?: string;
    parentId?: string;
};

export enum AddControlMode {
    EmptySlot,
    Sibling
}
export interface IAddModuleState {
    moduleId: string;
}

/**
 * Editable field component, containing all the logic for inline editable properties in editorial preview
 * Current support: text, image
 */
export class AddModuleControl extends React.Component<IAddModuleProps, IAddModuleState> {
    public constructor(props: IAddModuleProps) {
        super(props);
        this.state = {
            moduleId: props.moduleId
        };
    }
    public render(): JSX.Element | null {
        if (!this.props.enabled) {
            return null;
        }
        const localizedStrings = {
            insertModule:
                this.getLocalizedStringsfromTools('insertModule') !== ''
                    ? this.getLocalizedStringsfromTools('insertModule')
                    : 'Insert module',
            insertFragment:
                this.getLocalizedStringsfromTools('insertFragment') !== ''
                    ? this.getLocalizedStringsfromTools('insertFragment')
                    : 'Insert fragment',
            moveNotAllowed:
                this.getLocalizedStringsfromTools('moveNotAllowed') !== ''
                    ? this.getLocalizedStringsfromTools('moveNotAllowed')
                    : 'Move not allowed'
        };

        const button = (tooltip: string) => {
            return (
                <div
                    title={tooltip}
                    aria-label={tooltip}
                    className='add-module-button'
                    role='button'
                    id={this.getAddButtonId()}
                    onClick={this.toggleMenuDisplay}
                    onKeyDown={this.onKeyPressMenu}
                    tabIndex={0}
                >
                    <div className='add-module-icon'>
                        <svg viewBox='0 0 2048 2048'>
                            <path className='ms-add-module-icon' d='M1963 939v85h-939v939h-85v-939H0v-85h939V0h85v939h939z' />
                        </svg>
                    </div>
                    <div className='add-button-group' id={this.getAddButtonMenuId()}>
                        <button onClick={this.addModule} tabIndex={0} onKeyDown={this.onKeyPressAddModule}>
                            {localizedStrings.insertModule}
                        </button>
                        <button onClick={this.addFragment} tabIndex={0} onKeyDown={this.onKeyPressAddFragment}>
                            {localizedStrings.insertFragment}
                        </button>
                    </div>
                </div>
            );
        };
        if (this.props.mode === AddControlMode.Sibling) {
            return (
                <div className='add-sibling'>
                    <div className='add-ruler' />
                    <div className='add-sibling-inner'>{button(`Add a module to ${this.props.slotName || this.props.slotId}`)}</div>
                    <div className='add-ruler' />
                </div>
            );
        } else {
            const parentData = this.props.parentId ? `,p:${this.props.parentId}` : '';
            return (
                <div className='add-container' data-i={`{id:${this.props.moduleId},s:${this.props.slotId},t:empty-slot${parentData}}`}>
                    <div className='add-container-inner'>
                        <div className='default-buttons'>{button(`Configure ${this.props.slotName || this.props.slotId}`)}</div>
                        <div className='placeholder-drop'>
                            <div className='placeholder-drop-icon'>
                                <div className='ondrop-module placeholder-drop-icon-bg'>
                                    <svg viewBox='0 0 2048 2048'>
                                        <path
                                            className='ms-drop-module-slot-icon'
                                            // eslint-disable-next-line max-len
                                            d='M768 1664h128v128H768v-128zm256 0h128v128h-128v-128zm-512 0h128v128H512v-128zm1152-896h-128V640h128v128zm256-384v1664H256v-640H0V0h731l384 384h805zm-1152 0h165L768 219v165zm256 896V512H640V128H128v1152h896zm768-768h-640v896H640v128H512v-128H384v512h1408V512zm-384 1280h-128v-128h128v128zm256 0h-128v-128h128v128zm0-768h-128V896h128v128zm-256-256h-128V640h128v128zm256 512h-128v-128h128v128zm0 256h-128v-128h128v128z'
                                        />
                                    </svg>
                                </div>
                                <div className='ondrop-fragment placeholder-drop-icon-bg'>
                                    <svg viewBox='0 0 2048 2048'>
                                        <path
                                            className='ms-drop-module-slot-icon'
                                            // eslint-disable-next-line max-len
                                            d='M768 1664h128v128H768v-128zm256 0h128v128h-128v-128zm-512 0h128v128H512v-128zm1152-896h-128V640h128v128zm256-384v1664H256v-640H0V0h731l384 384h805zm-1152 0h165L768 219v165zm256 896V512H640V128H128v1152h896zm768-768h-640v896H640v128H512v-128H384v512h1408V512zm-384 1280h-128v-128h128v128zm256 0h-128v-128h128v128zm0-768h-128V896h128v128zm-256-256h-128V640h128v128zm256 512h-128v-128h128v128zm0 256h-128v-128h128v128z'
                                        />
                                    </svg>
                                </div>
                                <div className='ondrop-error placeholder-drop-icon-error-bg'>
                                    <svg viewBox='0 0 2048 2048'>
                                        <path
                                            className='ms-add-module-error-icon'
                                            // eslint-disable-next-line max-len
                                            d='M960 0q132 0 255 34t229 97 194 150 150 194 97 230 35 255q0 133-34 255t-97 229-150 194-194 150-230 97-255 35q-133 0-255-34t-229-97-194-150-150-194-97-230T0 960q0-132 34-255t97-229 150-194 194-150 230-97T960 0zm576 1024V896H384v128h1152z'
                                        />
                                    </svg>
                                </div>
                            </div>
                            <span className='ondrop-module'>{localizedStrings.insertModule}</span>
                            <span className='ondrop-fragment'>{localizedStrings.insertFragment}</span>
                            <span className='ondrop-error'>{localizedStrings.moveNotAllowed}</span>
                        </div>
                    </div>
                </div>
            );
        }
    }

    public toggleMenuDisplay = () => {
        const button = document.getElementById(this.getAddButtonId());
        const menu = document.getElementById(this.getAddButtonMenuId());
        if (button && menu) {
            if (menu.style.display === 'block') {
                menu.style.display = 'none';
            } else {
                menu.style.display = 'block';
            }
        }
    };

    public hideMenuDisplay = () => {
        const button = document.getElementById(this.getAddButtonId());
        const menu = document.getElementById(this.getAddButtonMenuId());
        if (button && menu) {
            menu.style.display = 'none';
        }
    };

    public getAddButtonId = () => {
        return this.props.index
            ? `add-${this.props.moduleId}-${this.props.slotId}-${this.props.index}`
            : `add-${this.props.moduleId}-${this.props.slotId}`;
    };

    public onKeyPressMenu = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            this.toggleMenuDisplay();
        } else if (event.key === 'Escape') {
            this.hideMenuDisplay();
        }
    };

    public onKeyPressAddModule = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            this.addModule();
        } else if (event.key === 'Escape') {
            this.hideMenuDisplay();
        }
    };

    public onKeyPressAddFragment = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            this.addFragment();
        } else if (event.key === 'Escape') {
            this.hideMenuDisplay();
        }
    };

    public getAddButtonMenuId = () => {
        return this.props.index
            ? `add-menu-${this.props.moduleId}-${this.props.slotId}-${this.props.index}`
            : `add-menu-${this.props.moduleId}-${this.props.slotId}`;
    };

    public addModule = () => {
        this.toggleMenuDisplay();
        const helper = getRenderingHelper();
        if (helper) {
            helper.addModule(this.props.moduleId, this.props.slotId, this.props.index || 0);
        }
    };

    public addFragment = () => {
        this.toggleMenuDisplay();
        const helper = getRenderingHelper();
        if (helper) {
            helper.addFragment(this.props.moduleId, this.props.slotId, this.props.index || 0);
        }
    };

    public getLocalizedStringsfromTools = (stringName: string): string => {
        const helper = getRenderingHelper();
        if (helper && typeof helper.getLocalizedString === 'function') {
            return helper.getLocalizedString(stringName);
        }
        return '';
    };
}
